// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import styles from "@mcmc/App/Widgets/Accordion/Accordion.styles";
import LazyImage from "@mcmc/MobiusOverrides/LazyImage";
import parse from "html-react-parser";
import React from "react";

export const enum fields {
    title = "title",
    imageUrl = "imageUrl",
    description = "description",
}

interface AccordionWidgetProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.imageUrl]: string;
        [fields.description]: string;
    };
}

type Props = AccordionWidgetProps;

const AccordionWidget: React.FC<Props> = ({ fields }) => {
    return (
        <Accordion headingLevel={1}>
            <AccordionSection headerProps={{ "aria-level": 1 }} title={fields.title}>
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.richContentWrapper}>
                        {parse(fields.description, parserOptions)}
                    </StyledWrapper>
                    {fields.imageUrl && <LazyImage src={fields.imageUrl} {...styles.image} altText={fields.title} />}
                </StyledWrapper>
            </AccordionSection>
        </Accordion>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            displayName: "Title",
            name: fields.title,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            displayName: "Description",
            name: fields.description,
            fieldType: "Contextual",
            editorTemplate: "RichTextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.imageUrl,
            fieldType: "Contextual",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            isRequired: false,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: AccordionWidget,
    definition,
};

export default widgetModule;
